/* prettier-ignore */

import { inject, Injectable } from '@angular/core';
import { CookieService } from "ngx-cookie-service";
import { LocalStorageService } from './localStorage.service';
import { PLANNER_MODE_STORAGE_KEY } from '../interfaces';

/**
 * Mecánica de la traducción automática de Google (widget translate_a/element.js).
 * Colaborador interno de TranslationService: los componentes no lo inyectan.
 * googtrans DEBE ser cookie (no localStorage): la lee el script de Google fuera de Angular.
 */
@Injectable({ providedIn: 'root' })

export class GoogleTranslateService {
  private cookieService: CookieService = inject(CookieService);
  private localStorageService = inject(LocalStorageService);

  public readonly availableLangs = [
    { name: "Shqip", code: "sq" },
    { name: "አማርኛ", code: "am" },
    { name: "العربية", code: "ar" },
    { name: "Հայերեն", code: "hy" },
    { name: "Azərbaycanca", code: "az" },
    { name: "Euskara", code: "eu" },
    { name: "Беларуская", code: "be" },
    { name: "বাংলা", code: "bn" },
    { name: "Bosanski", code: "bs" },
    { name: "Български", code: "bg" },
    { name: "Català", code: "ca" },
    { name: "简体中文", code: "zh-CN" },
    { name: "繁體中文", code: "zh-TW" },
    { name: "Corsu", code: "co" },
    { name: "Hrvatski", code: "hr" },
    { name: "Čeština", code: "cs" },
    { name: "Dansk", code: "da" },
    { name: "Español", code: "es" },
    { name: "Nederlands", code: "nl" },
    { name: "English", code: "en" },
    { name: "Eesti", code: "et" },
    { name: "Suomi", code: "fi" },
    { name: "Français", code: "fr" },
    { name: "Galego", code: "gl" },
    { name: "Deutsch", code: "de" },
    { name: "Ελληνικά", code: "el" },
    { name: "ગુજરાતી", code: "gu" },
    { name: "Kreyòl Ayisyen", code: "ht" },
    { name: "Magyar", code: "hu" },
    { name: "Íslenska", code: "is" },
    { name: "Indonesia", code: "id" },
    { name: "Gaeilge", code: "ga" },
    { name: "Italiano", code: "it" },
    { name: "日本語", code: "ja" },
    { name: "ಕನ್ನಡ", code: "kn" },
    { name: "ខ្មែរ", code: "km" },
    { name: "Kinyarwanda", code: "rw" },
    { name: "한국어", code: "ko" },
    { name: "Кыргызча", code: "ky" },
    { name: "Latina", code: "la" },
    { name: "Latviešu", code: "lv" },
    { name: "Lietuvių", code: "lt" },
    { name: "Lëtzebuergesch", code: "lb" },
    { name: "Македонски", code: "mk" },
    { name: "Malagasy", code: "mg" },
    { name: "Bahasa Melayu", code: "ms" },
    { name: "മലയാളം", code: "ml" },
    { name: "Malti", code: "mt" },
    { name: "मराठी", code: "mr" },
    { name: "Монгол", code: "mn" },
    { name: "မြန်မာ", code: "my" },
    { name: "नेपाली", code: "ne" },
    { name: "Norsk", code: "no" },
    { name: "پښتو", code: "ps" },
    { name: "Polski", code: "pl" },
    { name: "Português", code: "pt" },
    { name: "ਪੰਜਾਬੀ", code: "pa" },
    { name: "Română", code: "ro" },
    { name: "Русский", code: "ru" },
    { name: "Gagana Sāmoa", code: "sm" },
    { name: "Gàidhlig", code: "gd" },
    { name: "Српски", code: "sr" },
    { name: "Sesotho", code: "st" },
    { name: "Shona", code: "sn" },
    { name: "سنڌي", code: "sd" },
    { name: "සිංහල", code: "si" },
    { name: "Slovenčina", code: "sk" },
    { name: "Slovenščina", code: "sl" },
    { name: "Soomaali", code: "so" },
    { name: "Svenska", code: "sv" },
    { name: "Tagalog", code: "tl" },
    { name: "Тоҷикӣ", code: "tg" },
    { name: "Татарча", code: "tt" },
    { name: "ไทย", code: "th" },
    { name: "Türkçe", code: "tr" },
    { name: "Türkmençe", code: "tk" },
    { name: "Українська", code: "uk" },
    { name: "ئۇيغۇرچە", code: "ug" },
    { name: "Oʻzbekcha", code: "uz" },
    { name: "Tiếng Việt", code: "vi" },
    { name: "Cymraeg", code: "cy" }
  ];

  public isSupported(lang: string): boolean {
    return this.availableLangs.some((available) => available.code === lang);
  }

  public getCookieLang(): string | null {
    const cookie = this.cookieService.get('googtrans');
    if (!cookie)
      return null;

    const cookieSplit = cookie.split('/');
    return cookieSplit?.[cookieSplit.length - 1];
  }

  /** Escribe googtrans como '/{source}/{target}' en los tres scopes de dominio */
  public setCookie(sourceLang: string, targetLang: string): void {
    const baseDomain = this.getCurrentDomain();
    const fullDomain = this.getFullDomain();
    const value = `/${sourceLang}/${targetLang}`;

    this.clearCookie();

    document.cookie = `googtrans=${value}; path=/; domain=${baseDomain}; expires=Thu, 01 Jan 2030 00:00:00 UTC`;
    document.cookie = `googtrans=${value}; path=/; domain=${fullDomain}; expires=Thu, 01 Jan 2030 00:00:00 UTC`;
    // Cookie sin dominio específico (para el dominio actual)
    document.cookie = `googtrans=${value}; path=/; expires=Thu, 01 Jan 2030 00:00:00 UTC`;
  }

  /** Borra googtrans en los tres scopes. Nunca recarga: eso es de reloadPage() */
  public clearCookie(): void {
    const baseDomain = this.getCurrentDomain();
    const fullDomain = this.getFullDomain();

    this.cookieService.delete('googtrans', '/', baseDomain);
    this.cookieService.delete('googtrans', '/', fullDomain);

    document.cookie = `googtrans=; path=/; domain=${baseDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    document.cookie = `googtrans=; path=/; domain=${fullDomain}; expires=Thu, 01 Jan 1970 00:00:00 UTC`;
    document.cookie = "googtrans=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC";
  }

  /** Recarga para que el widget de Google aplique/retire la traducción */
  public reloadPage(): void {
    // El modo planner (embed) arranca por query param: la URL recargada debe conservarlo
    const plannerMode = this.localStorageService.getItem<boolean>(PLANNER_MODE_STORAGE_KEY);
    if (!plannerMode) {
      location.reload();
      return;
    }

    const urlToReload = `${window.location}/?planner=true`;
    window.location.assign(urlToReload);
  }

  /**
   * Obtiene el dominio base actual de forma dinámica.
   * Funciona con cualquier dominio (ciceroneweb.com, emtmadrid.es, localhost, etc.)
   */
  private getCurrentDomain(): string {
    const hostname = window.location.hostname;
    // Para localhost, devolver localhost directamente
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'localhost';
    }
    // Para otros dominios, obtener el dominio base (ej: emtmadrid.es de smartourism.emtmadrid.es)
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      return `.${parts.slice(-2).join('.')}`;
    }
    return hostname;
  }

  /**
   * Obtiene el dominio completo actual (incluyendo subdominio)
   */
  private getFullDomain(): string {
    return window.location.hostname;
  }
}
